import { useCallback, useEffect, useState } from "react";

import type { RequestRecord } from "../types";

import { HISTORY_LIMIT, STORAGE_KEYS } from "../constants";

const read = (): RequestRecord[] => {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEYS.history);
    return raw ? (JSON.parse(raw) as RequestRecord[]) : [];
  } catch {
    return [];
  }
};

const write = (entries: RequestRecord[]) => {
  globalThis.localStorage.setItem(
    STORAGE_KEYS.history,
    JSON.stringify(entries),
  );
};

const STORAGE_EVENT = "thredd-sandbox-history-change";

export const useRequestHistory = () => {
  const [entries, setEntries] = useState<RequestRecord[]>(() => read());

  useEffect(() => {
    const sync = () => setEntries(read());
    globalThis.addEventListener(STORAGE_EVENT, sync);
    globalThis.addEventListener("storage", sync);
    return () => {
      globalThis.removeEventListener(STORAGE_EVENT, sync);
      globalThis.removeEventListener("storage", sync);
    };
  }, []);

  const persist = useCallback((next: RequestRecord[]) => {
    write(next);
    setEntries(next);
    globalThis.dispatchEvent(new Event(STORAGE_EVENT));
  }, []);

  const record = useCallback(
    (entry: Omit<RequestRecord, "id" | "sentAt"> & { sentAt?: string }) => {
      const full: RequestRecord = {
        ...entry,
        id: `req_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        sentAt: entry.sentAt ?? new Date().toISOString(),
      };
      const next = [full, ...read()].slice(0, HISTORY_LIMIT);
      persist(next);
      return full;
    },
    [persist],
  );

  const clear = useCallback(() => persist([]), [persist]);

  return { entries, record, clear };
};
